import { useRef, useCallback } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useQuickReplies } from './useQuickReplies';
import { useWatch } from 'react-hook-form';
import { REPLIES } from '../HoldUntilReply.constants';

export type DragAndDropCallback = {
  addReplyVariant: (value: string, index: number) => void;
  removeReplyVariant: (index: number) => void;
  moveReplyVariant: (from: number, to: number) => void;
};

export const useDragAndDropCallbacks = () => {
  const { filteredQuickReplies } = useQuickReplies() as { filteredQuickReplies: string[] };
  const replies = useWatch({ name: REPLIES }) as { id: string; variants: { variant: string }[] }[];
  const dragAndDropRef = useRef({});

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;
      if (!destination) return;

      const childrenCurrent = dragAndDropRef?.current as Record<string, DragAndDropCallback>;
      const dragAndDropCallback = childrenCurrent?.[destination.droppableId];
      if (!dragAndDropCallback) return;
      if (source.droppableId === destination?.droppableId) {
        // Moving within the same reply list
        const dragAndDropCallback = childrenCurrent?.[destination.droppableId];
        dragAndDropCallback.moveReplyVariant(source.index, destination.index);
      } else if (source.droppableId === 'quick-replies') {
        // Adding from the ungrouped quick replies
        const value = filteredQuickReplies[source.index];
        dragAndDropCallback.addReplyVariant(value, destination.index);
      } else {
        // Adding from the reply list to another reply list
        const sourceReplyIndexMatch = source.droppableId.match(/reply-(\d+)/);
        const sourceReplyIndex = sourceReplyIndexMatch
          ? parseInt(sourceReplyIndexMatch[1], 10)
          : null;

        const destinationDragAndDropCallback = childrenCurrent?.[destination.droppableId];
        const sourceDragAndDropCallback = childrenCurrent?.[source.droppableId];
        if (!sourceDragAndDropCallback || !destinationDragAndDropCallback) return;
        if (sourceReplyIndex === null || sourceReplyIndex === undefined) return;

        const sourceReply = replies?.[sourceReplyIndex];
        const sourceReplyVariantIndex = source.index;

        const sourceReplyVariant = (sourceReply as { id: string; variants: { variant: string }[] })
          ?.variants[sourceReplyVariantIndex];

        if (!sourceReplyVariant) return;

        // add the item to the destination reply list
        destinationDragAndDropCallback.addReplyVariant(
          sourceReplyVariant.variant,
          destination.index,
        );

        // remove the item from the source reply list using callbacks
        sourceDragAndDropCallback.removeReplyVariant(sourceReplyVariantIndex);
      }
    },
    [filteredQuickReplies, replies],
  );

  const registerDragAndDropCallbacks = useCallback(
    (
      replyIndex: string,
      { addReplyVariant, removeReplyVariant, moveReplyVariant }: DragAndDropCallback,
    ) => {
      if (!dragAndDropRef.current) {
        dragAndDropRef.current = {};
      }

      const childrenCurrent = dragAndDropRef?.current as Record<string, DragAndDropCallback>;
      childrenCurrent[replyIndex] = {
        addReplyVariant,
        removeReplyVariant,
        moveReplyVariant,
      };
    },
    [],
  );

  return {
    onDragEnd,
    registerDragAndDropCallbacks,
  };
};
